/**
 * API error model (specs/c2-kontratlar.md 2.7). Every error body is
 * { error: { code, message, details? }, requestId }. The code always matches
 * ^[a-z0-9_]{1,64}$ (the bot's errorCodeOf rule); the message is Turkish, safe
 * to show, and never carries content or secrets.
 */
import { z } from 'zod';
export const ERROR_CODE_PATTERN = /^[a-z0-9_]{1,64}$/;
/** Every error code the API may return, with its HTTP status. New codes are added, never renamed. */
export const ERROR_STATUS = {
    invalid_request: 400,
    ambiguous_auth: 400,
    payload_too_large: 413,
    unauthenticated: 401,
    invalid_credentials: 401,
    login_expired: 401,
    wrong_surface: 403,
    unknown_human: 403,
    cannot_decide_tier: 403,
    t4_requires_panel_hold: 403,
    hold_required: 403,
    hold_invalid: 403,
    confirm_required: 403,
    confirm_invalid: 403,
    private_chat_required: 403,
    csrf: 403,
    db_forbidden: 403,
    totp_not_enrolled: 403,
    not_found: 404,
    not_pending: 409,
    expired: 409,
    payload_changed: 409,
    stale_view: 409,
    verifier_not_passed: 409,
    kill_switch_engaged: 409,
    already_applied: 409,
    already_reverting: 409,
    not_manual_pending: 409,
    hold_too_short: 409,
    hold_replayed: 409,
    confirm_too_fast: 409,
    confirm_expired: 409,
    confirm_replayed: 409,
    idempotency_conflict: 409,
    agent_disabled_by_config: 409,
    db_rule_violation: 409,
    edit_not_allowed: 422,
    edit_unchanged: 422,
    copy_not_confirmed: 422,
    policy_violation: 422,
    daily_cap_reached: 422,
    not_revertible: 422,
    not_available_in_c2: 422,
    outreach_no_active_campaign: 422,
    outreach_not_ready: 422,
    outreach_second_brand_wait: 422,
    outreach_personal_address_held: 422,
    attachment_mismatch: 422,
    connection_value_invalid: 422,
    connection_test_unavailable: 422,
    rate_limited: 429,
    login_locked: 429,
    internal: 500,
    db_unavailable: 503,
};
export const ERROR_CODES = Object.keys(ERROR_STATUS);
export const errorCodeSchema = z.enum(ERROR_CODES);
export function isErrorCode(value) {
    return Object.hasOwn(ERROR_STATUS, value);
}
/** Default Turkish messages. The API may say more in details; it never puts content or secrets here. */
export const ERROR_MESSAGES_TR = Object.freeze({
    invalid_request: 'İstek geçersiz.',
    payload_too_large: 'İstek gövdesi çok büyük.',
    ambiguous_auth: 'Panel ve bot kimliği aynı istekte gelemez.',
    unauthenticated: 'Kimlik doğrulanamadı.',
    invalid_credentials: 'Kullanıcı adı, parola veya kod hatalı.',
    login_expired: 'Giriş adımının süresi doldu. Kullanıcı adı ve parolayla baştan başla.',
    wrong_surface: 'Bu işlem bu yüzeyden yapılamaz.',
    unknown_human: 'Bu kullanıcı tanınmıyor veya aktif değil.',
    cannot_decide_tier: 'Bu katmanda karar verme yetkin yok.',
    t4_requires_panel_hold: 'Bu öneri sadece panelden, basılı tutarak onaylanır.',
    hold_required: 'Onay için basılı tutma gerekiyor.',
    hold_invalid: 'Basılı tutma belirteci geçersiz.',
    confirm_required: "Bu öneri Telegram'da iki adımda onaylanır: önce özeti aç, sonra onayla.",
    confirm_invalid: 'Onay belirteci geçersiz.',
    private_chat_required: 'Onay sadece botla özel sohbette verilir.',
    csrf: 'İstek kaynağı doğrulanamadı.',
    db_forbidden: 'Veritabanı bu işleme izin vermedi.',
    totp_not_enrolled: 'İki adımlı doğrulama kurulmamış. Sunucuda kurulum komutunu çalıştır.',
    not_found: 'Kayıt bulunamadı.',
    not_pending: 'Bu öneri artık beklemede değil.',
    expired: 'Önerinin süresi doldu.',
    payload_changed: 'İçerik ekranda gördüğünden farklı. Yenileyip tekrar bak.',
    stale_view: 'Ekrandaki bilgi bayat. Yenileyip tekrar dene.',
    verifier_not_passed: 'Denetçi onayı olmadan onaylanamaz.',
    kill_switch_engaged: 'Sistem durdurulmuş; önce devam ettir.',
    already_applied: 'Geri alma süresi geçti veya uygulama başladı.',
    already_reverting: 'Bu değişiklik için zaten bir geri dönüş var.',
    not_manual_pending: 'Bu işlem elle gönderim beklemiyor.',
    hold_too_short: 'Basılı tutma çok kısa sürdü.',
    hold_replayed: 'Bu basılı tutma belirteci zaten kullanıldı.',
    confirm_too_fast: 'Özeti okumak için birkaç saniye bekle, sonra onayla.',
    confirm_expired: 'Onay süresi doldu, özeti yeniden aç.',
    confirm_replayed: 'Bu onay zaten kullanıldı.',
    idempotency_conflict: 'Aynı istek anahtarı farklı içerikle geldi.',
    agent_disabled_by_config: 'Bu ajan yapılandırmada kapalı.',
    db_rule_violation: 'Veritabanı kuralı bu işlemi reddetti.',
    edit_not_allowed: 'Bu öneri düzeltilemez.',
    edit_unchanged: 'Metin değişmemiş.',
    copy_not_confirmed: 'Metnin kopyalandığı doğrulanmadı.',
    policy_violation: 'Politika sınırı aşılıyor.',
    daily_cap_reached: 'Bu işlem için günlük sınır doldu.',
    not_revertible: 'Bu değişiklik geri döndürülemez.',
    not_available_in_c2: 'Bu anahtar henüz açılamaz.',
    outreach_no_active_campaign: 'Etkin bir tanıtım kampanyası yok; önce Tanıtım gönderimi sayfasında kampanyayı onayla.',
    outreach_not_ready: 'Gönderim şartları tamam değil; Tanıtım gönderimi sayfası neyin beklediğini gösteriyor.',
    outreach_second_brand_wait: 'Bu firmaya ilk mail gitti; takip maili ilk mailden 14 gün sonra onaylanabilir (sayfada tarih yazıyor).',
    outreach_personal_address_held: 'Bu bir kişinin adresi; ilk dalgalarda yalnız firmaların genel iş adreslerine (info@, sales@ gibi) mail gidiyor.',
    attachment_mismatch: 'Ek dosya bilgileri uyuşmuyor.',
    connection_value_invalid: 'Değer beklenen biçimde değil; kopyaladığın değeri kontrol et.',
    connection_test_unavailable: 'Bu bağlantı için sunucu tarafında deneme yok.',
    rate_limited: 'Çok fazla istek. Biraz bekleyip tekrar dene.',
    login_locked: 'Çok fazla hatalı deneme. Giriş bir süre kilitli; bekleyip tekrar dene.',
    internal: 'Beklenmeyen bir hata oldu.',
    db_unavailable: 'Veritabanına ulaşılamıyor.',
});
/**
 * Codes after which the panel reloads the snapshot (getSnapshot) before asking for the
 * decision again (2.7). already_applied maps to the panel's AlreadyAppliedError instead.
 */
export const SNAPSHOT_REFRESH_CODES = Object.freeze([
    'payload_changed',
    'stale_view',
    'not_pending',
    'expired',
    'verifier_not_passed',
]);
/**
 * The error body. Clients parse the code as a pattern, not as the closed list, so a
 * code added later still reads (the bot shows unknown codes generically).
 */
export const apiErrorSchema = z.object({
    error: z.object({
        code: z.string().regex(ERROR_CODE_PATTERN),
        message: z.string(),
        details: z.record(z.string(), z.unknown()).optional(),
    }),
    requestId: z.string().min(1),
});
/** Builds an error body with the default message (the API adds the request id it sent in x-request-id). */
export function apiError(code, requestId, details) {
    const body = { error: { code, message: ERROR_MESSAGES_TR[code] }, requestId };
    if (details !== undefined)
        body.error.details = details;
    return body;
}
/** SQLSTATE of an unexpected trigger error mapped to the API error (2.5 step 13). */
export function errorCodeForSqlState(sqlState) {
    if (sqlState === '42501')
        return 'db_forbidden';
    if (sqlState === '23514')
        return 'db_rule_violation';
    return 'internal';
}
//# sourceMappingURL=errors.js.map